import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AtualizarDisponibilidadeDialog } from "@/components/freelancers/AtualizarDisponibilidadeDialog";
import type { EmpregoValue, InteresseValue } from "@/hooks/useAtualizarDisponibilidade";
import { formatCentsBRL } from "@/utils/money";
import {
  DIMENSION_LABELS,
  DIMENSION_VALUE_LABELS,
  NEGOCIACAO_STATUS_LABELS,
  type WhatsAppResumoListado,
} from "@/types/whatsapp";

const EIXOS = ["interesse_freela", "situacao_emprego"] as const;

function rotulo(valor: string): string {
  return DIMENSION_VALUE_LABELS[valor] ?? valor;
}

/**
 * "Pelo que a conversa indica": a leitura da IA sobre disponibilidade e
 * negociação. É hipótese, não fato: nada é gravado até a pessoa confirmar no
 * "Atualizar status", que abre o diálogo de sempre já preenchido.
 */
export function WhatsAppHipotese({ resumo }: { resumo: WhatsAppResumoListado }) {
  const queryClient = useQueryClient();
  const [dialogAberto, setDialogAberto] = useState(false);
  const hipotese = resumo.hipotese;
  if (!hipotese) return null;

  const disponibilidade = hipotese.disponibilidade;
  const divergencias = disponibilidade
    ? EIXOS.filter((eixo) => {
        const sugerido = disponibilidade[eixo];
        return sugerido && sugerido !== resumo.disponibilidade_atual[eixo];
      })
    : [];
  const negociacao = hipotese.negociacao;

  return (
    <div className="space-y-1 rounded-md border border-dashed px-3 py-2 text-sm">
      <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <Sparkles className="h-3 w-3" />
        Pelo que a conversa indica
      </div>
      {disponibilidade && (
        <p>
          {EIXOS.filter((eixo) => disponibilidade[eixo])
            .map((eixo) => `${DIMENSION_LABELS[eixo] ?? eixo}: ${rotulo(disponibilidade[eixo]!)}`)
            .join(" · ")}
          <span className="text-xs text-muted-foreground">
            {" "}
            (confiança {disponibilidade.confianca})
          </span>
        </p>
      )}
      {negociacao && (
        <p>
          Negociação: {NEGOCIACAO_STATUS_LABELS[negociacao.status] ?? negociacao.status}
          {negociacao.valor_centavos ? `, ${formatCentsBRL(negociacao.valor_centavos)}` : ""}
          {negociacao.vaga ? ` (${negociacao.vaga})` : ""}
          <span className="text-xs text-muted-foreground"> (confiança {negociacao.confianca})</span>
        </p>
      )}
      {(disponibilidade?.base || negociacao?.base) && (
        <p className="text-xs text-muted-foreground">
          {[disponibilidade?.base, negociacao?.base].filter(Boolean).join(" ")}
        </p>
      )}
      {divergencias.length > 0 && resumo.freelancer_id && (
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <span className="flex items-center gap-1 text-xs font-medium text-amber-700">
            <AlertTriangle className="h-3 w-3" />
            Difere do status atual (
            {divergencias
              .map((eixo) => {
                const atual = resumo.disponibilidade_atual[eixo];
                return `${DIMENSION_LABELS[eixo] ?? eixo}: ${atual ? rotulo(atual) : "sem registro"}`;
              })
              .join(" · ")}
            )
          </span>
          <Button size="sm" variant="outline" className="h-7" onClick={() => setDialogAberto(true)}>
            Atualizar status
          </Button>
        </div>
      )}

      {resumo.freelancer_id && disponibilidade && (
        <AtualizarDisponibilidadeDialog
          freelancerId={resumo.freelancer_id}
          freelancerNome={resumo.freelancer_nome ?? resumo.contact_name ?? "Freelancer"}
          open={dialogAberto}
          onOpenChange={setDialogAberto}
          inicial={{
            interesse: disponibilidade.interesse_freela as InteresseValue | null,
            emprego: disponibilidade.situacao_emprego as EmpregoValue | null,
            observacao: disponibilidade.base
              ? `Pela conversa de WhatsApp: ${disponibilidade.base}`
              : "Pela conversa de WhatsApp.",
          }}
          sourceRef={{
            tipo: "whatsapp_resumo",
            summary_id: resumo.resumo_id,
            conversation_id: resumo.conversation_id,
          }}
          onSaved={() => queryClient.invalidateQueries({ queryKey: ["whatsapp-resumos"] })}
        />
      )}
    </div>
  );
}
